import { useMemo } from 'react';
import { getLoanAppStep, LoanAppStep, LoanStepInputs } from 'merchant-api/derived';
import { getLDR } from 'merchant-api/spiceroad';
import { useApplication } from 'merchant-api/unified';
import {
  getProvePrefillDecision,
  isOptimizelyVariationEnabled,
  isProveStepChannel,
  PLAID_ACTIVE_CONNECTION_FLAG,
  PROVE_PREFILL_FLAG,
  provePrefillAttributes,
  useOptimizelyVariation,
  type ProvePrefillDecision,
} from 'merchant-api/vendor/optimizely';
import { useAnalytics } from 'use-analytics';
import { Application } from 'merchant-api/types';

// Single definition of the consent read, shared with the step mapper's gate (merchant-api/derived).
export { hasProvePrefillConsent } from 'merchant-api/derived';

/**
 * The ece_prove_prefill decision for the intake wizard (M3 rule, XF-10207). Evaluated only for NEW
 * applications on a PROVE_STEP channel, carrying the sales-channel attribute the M3 audiences match
 * on. Both the step mapper (first-visit routing + reload) and ProveStep (self-skip) read this, so the
 * two can never disagree about whether the applicant is in the test bucket.
 */
export const useProveStepDecision = (applicationData?: Application | null): {
  decision: ProvePrefillDecision;
  isLoading: boolean;
  proveStepEligible: boolean;
} => {
  const analytics = useAnalytics();
  const channelCode = applicationData?.metadata?.channelCode;
  const eligibleChannel = applicationData?.type === 'NEW' && isProveStepChannel(channelCode);
  const { data: variation, isLoading } = useOptimizelyVariation({
    featureKey: PROVE_PREFILL_FLAG,
    analytics,
    attributes: provePrefillAttributes(channelCode),
    enabled: eligibleChannel,
  });
  const decision = getProvePrefillDecision(variation);

  return {
    decision,
    isLoading: eligibleChannel && isLoading,
    proveStepEligible: eligibleChannel && decision.inProveStep,
  };
};

/**
 * Gathers the runtime inputs the shared status -> step mapper (getLoanAppStep) needs:
 * the open-bank-request LDR, for renewals the plaid-active-connection Optimizely flag, and for new
 * direct-like applications the Prove prefill (PROVE_STEP) bucket.
 */
const useLoanStepInputs = (): { inputs: LoanStepInputs; inputsLoading: boolean; inputsError: boolean } => {
  const { applicationData } = useApplication();
  const { hasOpenBankRequest, ldrError, ldrLoading } = getLDR();
  const isRenewal = applicationData?.type === 'RENEWAL';
  const { data: plaidActiveConnectionVariation, isLoading: isLoadingPlaidActiveConnection } = useOptimizelyVariation({ featureKey: PLAID_ACTIVE_CONNECTION_FLAG, analytics: useAnalytics(), enabled: isRenewal });
  const { proveStepEligible, isLoading: isLoadingProveDecision } = useProveStepDecision(applicationData);

  return {
    inputs: {
      hasOpenBankRequest,
      plaidActiveConnectionEnabled: isOptimizelyVariationEnabled(plaidActiveConnectionVariation),
      proveStepEligible,
    },
    inputsLoading: ldrLoading || (isRenewal && isLoadingPlaidActiveConnection) || isLoadingProveDecision,
    inputsError: ldrError,
  };
};

export const loanStep = (): {
  step?: LoanAppStep;
  stepLoading: boolean;
  stepError: boolean;
  applicationId?: string;
  applicationData?: Application;
  /** Applicant is in the M3 test bucket of ece_prove_prefill on a PROVE_STEP channel. */
  proveStepEligible: boolean;
} => {
  const { applicationData, isErrorApplication, isLoadingApplication } = useApplication();
  const { inputs, inputsLoading, inputsError } = useLoanStepInputs();

  const step = useMemo<LoanAppStep | undefined>(() => {
    if (!applicationData || inputsLoading || isLoadingApplication) return undefined;
    return getLoanAppStep(applicationData, inputs);
  }, [applicationData, inputs.hasOpenBankRequest, inputs.plaidActiveConnectionEnabled, inputs.proveStepEligible, inputsLoading, isLoadingApplication]);

  return {
    step,
    stepLoading: !step,
    stepError: isErrorApplication || inputsError,
    applicationId: applicationData?.id,
    applicationData: applicationData!,
    proveStepEligible: inputs.proveStepEligible === true,
  };
};

// Whether the applicant picked one of the Prove business matches, which switches the business
// steps to their review variants. Kept in sessionStorage keyed by application (not in the
// application data blob, where downstream PATCH responses overwrite the query cache and drop
// custom fields), so it survives step navigation and reloads for the life of the tab session.
const proveBusinessPrefillKey = (appId: string) => `proveBusinessPrefill:${appId}`;

export const markProveBusinessPrefill = (appId: string, selected: boolean) => {
  if (selected) sessionStorage.setItem(proveBusinessPrefillKey(appId), 'true');
  else sessionStorage.removeItem(proveBusinessPrefillKey(appId));
};

export const hasProveBusinessPrefill = (applicationData?: Application) =>
  Boolean(applicationData?.id && sessionStorage.getItem(proveBusinessPrefillKey(applicationData.id)));
